import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
    const { t } = useI18n();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [pwd, setPwd] = React.useState("");
    const [confirm, setConfirm] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(false);

    async function submit(e) {
        e.preventDefault();
        if (pwd.length < 8) return toast.error(t("profile_password_hint"));
        if (pwd !== confirm) return toast.error(t("profile_password_hint"));
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            toast.error(err.response?.data?.detail || t("reset_missing"));
        } finally { setBusy(false); }
    }

    return (
        <main className="py-16 min-h-[calc(100vh-5rem)] bg-background">
            <div className="max-w-md mx-auto px-6">
                <Card>
                    <CardContent className="p-8">
                        <div className="flex items-center gap-2 mb-2">
                            <KeyRound className="w-5 h-5 text-primary" />
                            <div className="overline text-brand-stone">{t("forgot_kicker")}</div>
                        </div>
                        <h1 className="font-serif text-3xl mb-1">{t("reset_title")}</h1>

                        {!token ? (
                            <div className="mt-6 flex items-start gap-2 text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm" data-testid="reset-missing-token">
                                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                                <div>{t("reset_missing")} <Link to="/forgot-password" className="underline">{t("reset_request_new")}</Link>.</div>
                            </div>
                        ) : done ? (
                            <div className="mt-6 flex items-start gap-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm" data-testid="reset-success">
                                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                                <div>{t("reset_success")}</div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-6 space-y-4">
                                <div>
                                    <Label>{t("profile_new_password")}</Label>
                                    <Input
                                        type="password"
                                        value={pwd}
                                        onChange={e => setPwd(e.target.value)}
                                        minLength={8}
                                        required
                                        autoFocus
                                        data-testid="reset-new-password-input"
                                        className="mt-1.5"
                                    />
                                    <div className="text-[11px] text-brand-stone mt-1 italic">{t("profile_password_hint")}</div>
                                </div>
                                <div>
                                    <Label>{t("profile_confirm_password")}</Label>
                                    <Input
                                        type="password"
                                        value={confirm}
                                        onChange={e => setConfirm(e.target.value)}
                                        minLength={8}
                                        required
                                        data-testid="reset-confirm-password-input"
                                        className="mt-1.5"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="reset-password-submit-btn"
                                    className="w-full bg-primary hover:bg-primary/90"
                                >
                                    {busy ? t("reset_updating") : t("reset_submit")}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </main>
    );
}
